/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import HeadPage from "pages/StepPages/StepFour/HeadPage";
import Badge from "components-new/Badge";
import TableNavigate from "pages/StepPages/StepFour/One/Tables/TableNavigate/TableNavigate";
import sass from "./tables.module.sass";
import { ReactComponent as InfoIcon } from "images/infoIcon.svg";
import { ReactComponent as ActionMenuIcon } from "images/ActionMenuIcon.svg";
import DropDownMenu from "pages/StepPages/StepFour/One/Tables/DropDownMenu";
import HelperText from "pages/StepPages/StepFour/One/Tables/HelperText";
import { useParams } from "react-router-dom";
import { useAppSelector } from "toolkit/hooks";
import ModalPortal from "components-new/ModalPortal/index";
import { Button } from "components/shared/index";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import ModalFilledTables from "pages/StepPages/StepFour/One/Tables/ModalFilledTables";
import { ModalTemplateModel } from "./TableNavigate/DescriptionTable/Modals";
import { organizationAPI } from "libs/apiClient/Organization";
import { statusesAPI } from "libs/apiClient/Statuses";
import {
	ActualThreatResponse,
	OrganizationObjectThreatResponse,
	SystemDescriptionResponse,
} from "libs/apiClient/data-contracts";

interface IContext {
	container: any;
	selectFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
	actual: ActualThreatResponse[];
	currentThread: OrganizationObjectThreatResponse | undefined;
	description: SystemDescriptionResponse;
	confirm: boolean;
	openConfirm: () => void;
	setConfirm: any;
	requiredFields: boolean;
	checkTables: (data: { type: "pattern" | "model"; ok: (() => void) | null }) => void;
}

/**
 * Контекст для таблиц
 * При первой загрузке уходит несколько запросов за данными для таблиц
 */
export const CreateContext = createContext<IContext>({} as IContext);

const Tables = () => {
	/**
	 * Состояние активного объекта угрозы
	 */
	const [currentThreat, setCurrentThreat] = useState<OrganizationObjectThreatResponse>();

	/**
	 * Флаг для определения первой загрузки
	 */
	const oneLoad = useRef(false);

	/**
	 *  реф на иконку с точками и стэйт для дробдауна для этой иконки
	 */
	const refDrop = useRef<any>(null);
	const [dropMenu, setDropMenu] = useState(false);
	const dropDownMenuRef = useRef<HTMLDivElement | null>(null);

	/**
	 *  стэйт для выпадающей подсказки
	 */
	const [helpMenu, setHelpMenu] = useState(true);
	const helpContentRef = useRef<HTMLDivElement>();

	/**
	 *  данные для общего контекста таблиц
	 */
	const [context, setContext] = useState<Omit<IContext, "container">>({} as Omit<IContext, "container">);

	const params = useParams() as { threadId: string };

	const { id: userID } = useAppSelector((state) => state.profile.profile);

	const [requiredFields, setRequiredFields] = useState(false);

	/**
	 *  собственно сами запросы за данными
	 */
	useEffect(() => {
		if (params.threadId) {
			organizationAPI.objectThreatDetail(+params.threadId).then(({ data: threadData }) => {
				setCurrentThreat(threadData);
				confirm.current = threadData?.threatStatus === "APPROVED";
				setContext((prevState) => ({ ...prevState, currentThread: threadData, confirm: confirm.current }));
			});
			organizationAPI.objectSystemDetail(+params.threadId).then(({ data }) => {
				setContext((prevState) => ({ ...prevState, description: data }));
			});
			organizationAPI.objectThreatsActualDetail(+params.threadId).then(({ data }) => {
				setContext((prevState) => ({ ...prevState, actual: data }));
			});
		}
	}, [params.threadId]);

	const selectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length) {
			// const file = new FormData();
			// file.append("file", e.target.files[0]);
			// const blobFile = new Blob(e.target.files[0] as Blob);
			const file = e.target.files[0];
			organizationAPI.objectDocumentVerifyCreate(+params.threadId, { userID: +userID! }, { file }).then(() => {
				// refetchGetThread().then(({ data: currentThread }) => {
				organizationAPI.objectThreatDetail(+params.threadId).then(({ data }) => {
					setCurrentThreat(data);
					confirm.current = true;
					setContext((prevState) => ({ ...prevState, currentThread: data, confirm: confirm.current }));
				});
				// });
				setRequiredFields(false);
			});
			// uploadFile({ id: +params.threadId, body, userId: +userID! })
			// 	.then(() => {})
			// 	.catch(() => {
			// 		setRequiredFields(true);
			// 	});
			// $api
			// 	.post(`/organization/object/${params.threadId}/document/verify`, file, { params: { userID } })
			// 	.then(() => {})
			// 	.catch(() => {});
			e.target.files = null;
			e.target.value = "";
		}
	};

	/**
	 *  эффект вешает на документ прослушивание клика,
	 *  и закрывает вкладку с выпадающим меню и подсказкой если кликнули не по ним или не по иконкам
	 */
	useEffect(() => {
		function handleClickOutside(event: any) {
			if (
				refDrop.current &&
				dropDownMenuRef.current &&
				!dropDownMenuRef.current.contains(event.target) &&
				!refDrop.current.contains(event.target)
			) {
				setDropMenu(false);
			}
			if (oneLoad.current && helpContentRef.current && !helpContentRef.current.contains(event.target)) {
				setHelpMenu(false);
			}
			oneLoad.current = true;
		}

		document.addEventListener("click", handleClickOutside);
		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
		//eslint-disable-next-line
	}, [dropDownMenuRef.current, refDrop.current]);

	/**
	 *  Содержит ссылку на элемент в разметке и передается в контекст,
	 *  для того чтобы в другом компоненте можно было передать в реф дополнительные элементы
	 */
	const createRef = useRef<any>();

	const confirm = useRef(false);

	let closeDropMenu = () => {
		setDropMenu(false);
	};

	const [confirmModal, setConfirmModal] = useState(false);

	const resetStatus = () => {
		statusesAPI.objectThreatsStatusResetCreate(+params.threadId).then(({ data }) => {
			setCurrentThreat(data!);
			confirm.current = false;
			setConfirmModal(false);
			setContext((prevState) => ({ ...prevState, currentThread: data, confirm: confirm.current }));
		});
	};

	const [filledTable, setFilledTable] = useState<{
		active: boolean;
		type: "pattern" | "model";
		ok: (() => void) | null;
	}>({
		active: false,
		type: "pattern",
		ok: null,
	});

	const closeClickModal = () => setFilledTable({ active: false, ok: null, type: "pattern" });

	const checkTables = useCallback(
		(modal: { type: "pattern" | "model"; ok: (() => void) | null }) => {
			// loadCheckTable({ id: +params.threadId }).then((r) => {
			// 	if ("data" in r) {
			setFilledTable({ ...modal, active: true });
			// } else {
			// 	modal.ok && modal.ok();
			// }
			// });
			//eslint-disable-next-line
		},[params.threadId],
	);

	const [patternModal, setPatternModal] = useState<{
		active: boolean;
		data: number | null;
		mode: "create" | "select";
	}>({
		active: false,
		mode: "create",
		data: null,
	});

	const closePatternModal = () => setPatternModal((prevState) => ({ ...prevState, active: false, data: null }));
	return (
		<>
			<HeadPage
				topTitle={"4. Формирование модели угроз"}
				subTitle={
					<div className={sass.subTitle}>
						<p>{currentThreat?.name || null}</p>
						{currentThreat && currentThreat.threatStatus === "APPROVED" ? (
							<p>
								Загрузил утвержденную версию:{" "}
								<span className={sass.link}>
									{currentThreat?.threatAuthor?.lastName} {currentThreat?.threatAuthor?.firstName}{" "}
									{currentThreat?.threatAuthor?.middleName}
								</span>
							</p>
						) : null}
					</div>
				}
				title={
					<div className={sass.statusDoc}>
						<p>Формирование модели угроз</p>
						{currentThreat && currentThreat.threatStatus === "APPROVED" ? (
							<Badge>Утверждена {currentThreat.approvalDate}</Badge>
						) : (
							<Badge type={"error"}>Не утвержден</Badge>
						)}
					</div>
				}>
				<div className={sass.top}>
					<div ref={createRef} className={sass.btn}>
						<div className={sass.icon}>
							<InfoIcon
								onClick={(e) => {
									e.stopPropagation();
									setHelpMenu(!helpMenu);
								}}
							/>
							<HelperText toggle={helpMenu} ref={helpContentRef} />
						</div>
						<Button
							variant={BUTTON_VARIANTS.OUTLINED}
							onClick={() => setPatternModal({ active: true, data: Number(params.threadId), mode: "create" })}>
							Сохранить как шаблон
						</Button>
					</div>

					<ActionMenuIcon onClick={() => setDropMenu(!dropMenu)} ref={refDrop} />
					<DropDownMenu
						checkTables={checkTables}
						applyPattern={() => setPatternModal({ active: true, data: Number(params.threadId), mode: "select" })}
						categoryExist={currentThreat?.categoryExists}
						confirm={confirm.current}
						openConfirm={() => setConfirmModal(true)}
						thread={currentThreat}
						table={!!context.actual?.length}
						setActual={(actual) => setContext((prevState) => ({ ...prevState, actual }))}
						close={closeDropMenu}
						toggle={dropMenu}
						target={refDrop}
						ref={dropDownMenuRef}
					/>
				</div>
			</HeadPage>
			<ModalTemplateModel target={patternModal} close={closePatternModal} />
			<ModalFilledTables
				trigger={filledTable.active}
				type={filledTable.type}
				close={closeClickModal}
				ok={filledTable.ok}
			/>
			{confirmModal && (
				<ModalPortal
					subtitle={
						"Данная модель угроз имеет статус Утвержден. Изменение, или дополнение любой информации повлечёт за собой отмену статуса утверждения документа"
					}
					handleClose={() => setConfirmModal(false)}
					title={"Продолжить редактирование?"}>
					<div className={sass.btn}>
						<Button onClick={() => setConfirmModal(false)} variant={BUTTON_VARIANTS.OUTLINED}>
							Отмена
						</Button>
						<Button onClick={resetStatus} variant={BUTTON_VARIANTS.CONTAINED}>
							Продолжить
						</Button>
					</div>
				</ModalPortal>
			)}
			<CreateContext.Provider
				value={{
					container: createRef,
					...context,
					selectFile,
					checkTables,
					setConfirm: setContext,
					openConfirm: () => setConfirmModal(true),
					requiredFields: requiredFields,
				}}>
				<TableNavigate thread={currentThreat?.id} />
			</CreateContext.Provider>
		</>
	);
};

export default Tables;
